import type { ReactNode } from 'react'
import cx from 'classnames'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { Spinner } from '@/components/Spinner'
import styles from './ListItem.module.css'

interface ListItemProps {
  children?: ReactNode
  onPress?(): unknown
  isDisabled?: boolean
  /**
   * Exposes the item as a toggle button in this state.
   */
  pressed?: boolean
  /**
   * If true, does not round the first item.
   */
  hasListHeader?: boolean
  testId?: string
}

/**
 * A row in a list; a button when it has an `onPress`.
 */
export function ListItem({
  children,
  onPress,
  hasListHeader,
  isDisabled,
  pressed,
  testId,
}: ListItemProps) {
  const className = cx(
    styles.root,
    hasListHeader && styles.hasListHeader,
    onPress && styles.clickable,
  )
  if (!onPress) {
    return (
      <div className={className} data-test={testId}>
        {children}
      </div>
    )
  }

  return (
    <RawButton
      className={className}
      aria-pressed={pressed}
      // Not `isDisabled`: a disabled button drops focus while a toggle is pending.
      aria-disabled={isDisabled || undefined}
      onPress={isDisabled ? undefined : onPress}
      testId={testId}
    >
      {children}
    </RawButton>
  )
}

interface ListItemVisualProps {
  children?: ReactNode
}

/**
 * The avatar or icon at the start of a list item.
 */
export function ListItemVisual({ children }: ListItemVisualProps) {
  return <div className={styles.visual}>{children}</div>
}

interface ListItemContentProps {
  children?: ReactNode
}

/**
 * The text of a list item.
 */
export function ListItemContent({ children }: ListItemContentProps) {
  return <div className={styles.content}>{children}</div>
}

interface ListItemTitleProps {
  children?: ReactNode
  title?: string
}

/**
 * The list item's primary line.
 */
export function ListItemTitle({ title, children }: ListItemTitleProps) {
  return (
    <p title={title} className={styles.title}>
      {children}
    </p>
  )
}

interface ListItemDescriptionProps {
  children?: ReactNode
  title?: string
}

/**
 * The list item's secondary line.
 */
export function ListItemDescription({
  title,
  children,
}: ListItemDescriptionProps) {
  return (
    <p title={title} className={styles.description}>
      {children}
    </p>
  )
}

interface ListItemExtrasProps {
  children?: ReactNode
}

/**
 * Content at the end of a list item.
 */
export function ListItemExtras({ children }: ListItemExtrasProps) {
  return <div className={styles.extras}>{children}</div>
}

interface ListItemCheckboxProps {
  checked: boolean
  isDisabled?: boolean
  isPending?: boolean
}

/**
 * The checked state of a clickable `ListItem`. Purely visual, since the item
 * itself is the control.
 */
export function ListItemCheckbox({
  checked,
  isPending,
  isDisabled,
}: ListItemCheckboxProps) {
  return (
    <span
      aria-hidden
      className={cx(
        styles.checkbox,
        isDisabled && styles.checkboxDisabled,
        isPending && styles.checkboxLoading,
      )}
    >
      {isPending ? (
        <Spinner size={20} />
      ) : (
        <Icon
          color="var(--theme-base)"
          name={checked ? 'checkbox' : 'checkbox-blank'}
          size={24}
        />
      )}
    </span>
  )
}
